import { IntlShape } from 'react-intl';
import { OrganizationSettings, Person } from 'types';

export const TEMPLATE_TYPES = {
  ONE_ON_ONE_AGENDA: 'ONE_ON_ONE_AGENDA',
  DEVELOPMENT_PLAN: 'DEVELOPMENT_PLAN',
  PROMOTION_PACKET_QUESTION: 'PROMOTION_PACKET_QUESTION',
} as const;

export type TemplateType = (typeof TEMPLATE_TYPES)[keyof typeof TEMPLATE_TYPES];

export const TEMPLATE_STATUSES = {
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
} as const;

export type TemplateStatus =
  (typeof TEMPLATE_STATUSES)[keyof typeof TEMPLATE_STATUSES];

export interface AgendaTemplateContent {
  items: string[];
  category?: string | null;
}

export interface DevelopmentPlanTemplateContent {
  value: string;
  candidate_can_manage: boolean;
}

export interface PromotionPacketQuestionContent {
  question: {
    name?: string;
    label?: string;
    type?: string;
    required?: boolean;
    helperText?: string;
    [key: string]: unknown;
  };
}

export type TemplateContent =
  | AgendaTemplateContent
  | DevelopmentPlanTemplateContent
  | PromotionPacketQuestionContent;

export interface ContentTemplate {
  id: string;
  type: TemplateType;
  name: string;
  status: TemplateStatus;
  content: TemplateContent;
  updated_at: string | null;
  updated_by: Person | null;
}

export type ContentTemplateMutationResponse = ContentTemplate & {
  organization_settings?: Partial<OrganizationSettings>;
};

export const TEMPLATE_TYPE_NAME = (
  formatMessage: IntlShape['formatMessage'],
  type: TemplateType
): string => {
  switch (type) {
    case TEMPLATE_TYPES.ONE_ON_ONE_AGENDA:
      return formatMessage({
        id: 'app.views.administration.content_administration.templates.type.one_on_one_agenda',
        defaultMessage: '1:1 agenda',
      });
    case TEMPLATE_TYPES.DEVELOPMENT_PLAN:
      return formatMessage({
        id: 'app.views.administration.content_administration.templates.type.development_plan',
        defaultMessage: 'Development plan',
      });
    case TEMPLATE_TYPES.PROMOTION_PACKET_QUESTION:
      return formatMessage({
        id: 'app.views.administration.content_administration.templates.type.promotion_packet_question',
        defaultMessage: 'Promotion packet question',
      });
  }
};

export const TEMPLATE_TYPE_PLURAL_NAME = (
  formatMessage: IntlShape['formatMessage'],
  type: TemplateType
): string => {
  switch (type) {
    case TEMPLATE_TYPES.ONE_ON_ONE_AGENDA:
      return formatMessage({
        id: 'app.views.administration.content_administration.templates.type_plural.one_on_one_agenda',
        defaultMessage: '1:1 agenda templates',
      });
    case TEMPLATE_TYPES.DEVELOPMENT_PLAN:
      return formatMessage({
        id: 'app.views.administration.content_administration.templates.type_plural.development_plan',
        defaultMessage: 'Development plan templates',
      });
    case TEMPLATE_TYPES.PROMOTION_PACKET_QUESTION:
      return formatMessage({
        id: 'app.views.administration.content_administration.templates.type_plural.promotion_packet_question',
        defaultMessage: 'Promotion packet questions',
      });
  }
};

// Availability copy. Templates are referenced live: editing one changes what
// every 1:1, development plan or promotion packet still referencing it shows.
export const TEMPLATE_TYPE_AVAILABILITY = (
  formatMessage: IntlShape['formatMessage'],
  type: TemplateType
): string => {
  switch (type) {
    case TEMPLATE_TYPES.ONE_ON_ONE_AGENDA:
      return formatMessage({
        id: 'app.views.administration.content_administration.templates.availability.one_on_one_agenda',
        defaultMessage:
          'Available when agenda items are added to a 1:1. Archived templates are no longer offered.',
      });
    case TEMPLATE_TYPES.DEVELOPMENT_PLAN:
      return formatMessage({
        id: 'app.views.administration.content_administration.templates.availability.development_plan',
        defaultMessage:
          'Available when a new development plan is created. Changes to a template apply to new plans, and to an existing plan only when it is reset to the template. Archiving hides a template from new plans, while existing plans keep it.',
      });
    case TEMPLATE_TYPES.PROMOTION_PACKET_QUESTION:
      return formatMessage({
        id: 'app.views.administration.content_administration.templates.availability.promotion_packet_question',
        defaultMessage:
          'Asked to people giving feedback on a promotion packet. Archived questions are no longer asked; answers already given are kept.',
      });
  }
};
